import React, { useState, useEffect } from 'react'
import styles from './SelectedFile.module.css'
import { FILE_TYPE_PHP, FILE_TYPE_HTML, FILE_TYPE_PDF } from '../../constants'
import DragDropArea from '../DragDrop/DragDrop'
import { createFileInput, isTotalSizeWithinLimit, isPhpFile, isValidFileSize } from 'utils/general'
import { PRO_MAX_FILE_SIZES, PLANS } from 'constants/plans'
import { DROPZONE_ACCEPTED_FILES, DROPZONE_ACCEPTED_FILES_PRO } from '../../../../constants'
import FileListModal from 'components/FileListModal/FileListModal'
import FileListView from '../FileListView/FileListView'
import {
  findHtmlFilesInZipFile,
  isIndexFile,
  isIndexFileExisting,
  sortAllHtmlFiles,
  getPhpFiles,
  getFileType,
  getFileContentType
} from 'helpers/file-type'
import { TLDS } from 'constants'

const FILE_ICONS = {
  [FILE_TYPE_HTML]: 'html-file.png',
  [FILE_TYPE_PDF]: 'pdf-file.png',
  [FILE_TYPE_PHP]: 'php-file.png',
  'application/zip': 'zip-folder.png',
  'application/x-zip-compressed': 'zip-folder.png',
  'application/octet-stream': 'zip-folder.png'
}

export const isZipFile = (fileType) => {
  return (
    fileType === 'application/zip' ||
    fileType === 'application/x-zip-compressed' ||
    fileType === 'application/octet-stream'
  )
}

const SelectedFile = ({
  selectedFile,
  addedFiles = [],
  draggedFile,
  indexFile,
  fileUploadLimit,
  productId,
  setFieldValue,
  className = '',
  maxFileSizeMB = 16,
  domainValues,
  showUpgradeModal
}) => {
  const [showFileList, setShowFileList] = useState(false)
  const [htmlFiles, setHtmlFiles] = useState([])
  const [phpFiles, setPhpFiles] = useState([])
  const [acceptedFiles, setAcceptedFiles] = useState(
    getFileContentType(selectedFile?.name) || selectedFile?.fileType || selectedFile?.type
  )

  const allFiles = selectedFile ? [selectedFile, ...addedFiles] : []

  const zipFileUploaded = async (file) => {
    try {
      const filesFound = await findHtmlFilesInZipFile(file)
      const { containsPhpFile, phpFiles } = await getPhpFiles(file)

      if (containsPhpFile && phpFiles?.length > 0) {
        setPhpFiles(phpFiles)
        setShowFileList(containsPhpFile)
      } else {
        setHtmlFiles(filesFound)
        setShowFileList(!isIndexFile(filesFound))
      }
    } catch (error) {
      console.error('Error processing ZIP file:', error)
    }
  }

  const isNewHtmlFilesUploaded = (files) => {
    let currentIndex = indexFile

    files.forEach((file) => {
      if ((file.name || file.path) === 'index.html') {
        currentIndex = file.name || file.path
        setFieldValue('uploadValues.indexFile', currentIndex)
      }
    })

    if (files.length > 1 && !isIndexFileExisting(files)) {
      const htmlFilesFound = sortAllHtmlFiles(files)
      setHtmlFiles(htmlFilesFound)

      if (htmlFilesFound.length > 1) {
        setShowFileList(true)
      } else if (htmlFilesFound.length === 1) {
        setFieldValue('uploadValues.indexFile', htmlFilesFound[0])
      }
    }
  }

  useEffect(() => {
    const checkZipFile = async () => {
      if (selectedFile && isZipFile(selectedFile.type || selectedFile.fileType)) {
        await zipFileUploaded(selectedFile)
      }
    }

    checkZipFile()
  }, [selectedFile])

  const handleFileDelete = (file, index) => {
    if (index === 0) {
      if (addedFiles && addedFiles.length > 0) {
        const newMainFile = addedFiles[0]
        const remainingFiles = addedFiles.slice(1)

        setFieldValue('uploadValues.selectedFile', newMainFile)
        setFieldValue('uploadValues.fileType', getFileType(newMainFile.name))
        setFieldValue('uploadValues.addedFiles', remainingFiles)

        if (isZipFile(newMainFile.type)) {
          zipFileUploaded(newMainFile)
        }
      } else {
        setFieldValue('uploadValues.selectedFile', null)
        setFieldValue('uploadValues.fileType', null)
        setFieldValue('uploadValues.indexFile', null)
        setFieldValue('uploadValues.isZipPhpFile', false)

        setHtmlFiles([])
        setPhpFiles([])
      }
    } else {
      const updatedAdditionalFiles = [...addedFiles]
      updatedAdditionalFiles.splice(index - 1, 1)
      setFieldValue('uploadValues.addedFiles', updatedAdditionalFiles)

      isNewHtmlFilesUploaded([selectedFile, ...updatedAdditionalFiles])
    }
  }

  const handleFilesSelected = async (files) => {
    if (!files || !files.length) return

    const maxSizeMB = fileUploadLimit || PRO_MAX_FILE_SIZES[productId] || maxFileSizeMB

    const mainFile = files[0]

    if (!isValidFileSize(mainFile, maxSizeMB)) {
      if (showUpgradeModal) {
        showUpgradeModal(mainFile, maxSizeMB)
      } else {
        alert(`File should be less than ${maxSizeMB}MB on your current plan.`)
      }
      return
    }

    setFieldValue('uploadValues.selectedFile', mainFile)
    setFieldValue('uploadValues.fileType', getFileType(mainFile.name))
    setAcceptedFiles(mainFile.type)

    if (isZipFile(mainFile.type)) {
      await zipFileUploaded(mainFile)
    } else if (isPhpFile(mainFile)) {
      if (domainValues) {
        setFieldValue('domainValues.domainSuffix', TLDS.TIINY_IO)
        setFieldValue('domainValues.tlds', [TLDS.TIINY_IO])
      }
    }

    if (files.length > 1) {
      const additionalFiles = files.slice(1)
      const allFileNames = new Set([mainFile.name])
      let duplicateFound = false

      additionalFiles.forEach((file) => {
        if (allFileNames.has(file.name)) {
          duplicateFound = true
        }
        allFileNames.add(file.name)
      })

      if (duplicateFound) {
        alert('Please upload files with different names')
        return
      }

      const totalSize = [mainFile, ...additionalFiles].reduce((sum, file) => sum + file.size, 0) / (1024 * 1024)
      if (totalSize > maxSizeMB) {
        if (showUpgradeModal) {
          showUpgradeModal(mainFile, maxSizeMB, true)
        } else {
          alert(`Total files should be less than ${maxSizeMB}MB on your current plan.`)
        }
        return
      }

      setFieldValue('uploadValues.addedFiles', additionalFiles)
    }

    isNewHtmlFilesUploaded(files.length > 1 ? files : [mainFile])
  }

  const handleAddMoreFiles = (e) => {
    if (e) e.preventDefault()

    const maxSizeMB = fileUploadLimit || PRO_MAX_FILE_SIZES[productId] || maxFileSizeMB
    const input = createFileInput(productId === PLANS.PRO_U?.id ? DROPZONE_ACCEPTED_FILES_PRO : DROPZONE_ACCEPTED_FILES)

    input.addEventListener('change', (event) => {
      const fileToAdd = event.target.files[0]
      if (!fileToAdd) return

      const isFileNameUnique = (existingFiles, newFile) => {
        if (!newFile || !existingFiles) return true

        const newFileName = newFile.name || ''

        if (!newFileName) return true

        return !existingFiles.some((file) => {
          if (!file) return false
          const existingName = file.name || file.path || file.s3FileName || ''
          return existingName.toLowerCase() === newFileName.toLowerCase()
        })
      }

      if (!isFileNameUnique([selectedFile, ...addedFiles], fileToAdd)) {
        alert('Please upload files with different names')
        return
      }

      if (isTotalSizeWithinLimit(fileToAdd, [selectedFile, ...addedFiles], maxSizeMB)) {
        const updatedFiles = [...addedFiles, fileToAdd]
        setFieldValue('uploadValues.addedFiles', updatedFiles)

        isNewHtmlFilesUploaded([selectedFile, ...updatedFiles])
      } else if (showUpgradeModal) {
        showUpgradeModal(fileToAdd, maxSizeMB, true)
      } else {
        alert(`Total files should be less than ${maxSizeMB}MB on your current plan.`)
      }
    })

    input.click()
  }

  const transformFiles = (files) => {
    return files.map((file) => ({
      ...file,
      icon: FILE_ICONS[file.type || file.fileType] || 'file-x.svg',
      name: file.path || file.name || file.fileName || 'Unknown file',
      size: file.size || 0,
      type: file.type || file.fileType || 'application/octet-stream'
    }))
  }

  const handleFileListDelete = (file) => {
    const index = allFiles.findIndex(
      (f) => (f.name || f.path || f.fileName) === (file.name || file.path || file.fileName)
    )
    handleFileDelete(file, index)
  }

  useEffect(() => {
    const handleDraggedFile = async (draggedFile) => {
      const file = draggedFile

      const maxSizeMB = fileUploadLimit || PRO_MAX_FILE_SIZES[productId] || maxFileSizeMB

      if (isValidFileSize(file, maxSizeMB)) {
        const processFile = async () => {
          if (isZipFile(file.type)) {
            await zipFileUploaded(file)
          } else if (isPhpFile(file)) {
            setFieldValue('domainValues.domainSuffix', TLDS.TIINY_IO)
            setFieldValue('domainValues.tlds', [TLDS.TIINY_IO])
          }

          setFieldValue('uploadValues.selectedFile', file)
          setFieldValue('uploadValues.fileType', getFileType(file.name))
          setAcceptedFiles(file.type)
          setFieldValue('uploadValues.draggedFile', null)

          isNewHtmlFilesUploaded([file])
        }

        processFile()
      } else if (showUpgradeModal) {
        showUpgradeModal(file, maxSizeMB)
      } else {
        alert(`File should be less than ${maxSizeMB}MB on your current plan.`)
      }
    }

    if (draggedFile) {
      handleDraggedFile(draggedFile)
    }
  }, [draggedFile])

  const shouldShowAddButton =
    selectedFile && !isZipFile(selectedFile?.type || selectedFile?.fileType) && !isPhpFile(selectedFile)

  const transformedFiles = transformFiles(allFiles)

  if (allFiles.length === 0) {
    return (
      <DragDropArea
        onFileSelected={handleFilesSelected}
        maxFileSize={maxFileSizeMB}
        acceptedFiles={productId === PLANS.PRO_U.id ? DROPZONE_ACCEPTED_FILES_PRO : DROPZONE_ACCEPTED_FILES}
      />
    )
  }

  return (
    <>
      {/* Replace the old file display with the new FileListView component */}
      <FileListView
        key={allFiles.length}
        files={transformedFiles}
        onDelete={handleFileListDelete}
        indexFile={indexFile}
        maxVisibleFiles={3}
        className={className}
      />

      {/* Add more files button */}
      {shouldShowAddButton && (
        <button
          type="button"
          className={styles.addFilesBtn}
          onClick={(e) => {
            e.preventDefault()
            handleAddMoreFiles()
          }}
          aria-label="Add more files"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 12H19" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Add more files
        </button>
      )}

      {/* HTML File Picker Modal */}
      {htmlFiles?.length > 0 && (
        <FileListModal
          show={showFileList}
          onHide={() => setShowFileList(false)}
          onFileSelected={(file) => {
            setFieldValue('uploadValues.indexFile', file)
            setShowFileList(false)
          }}
          filesList={htmlFiles}
          indexFile={indexFile}
          title="Which file should be your homepage?"
          subtitle="We've found multiple HTML files in your Zip"
        />
      )}

      {/* PHP File Picker Modal */}
      {phpFiles?.length > 0 && (
        <FileListModal
          show={showFileList}
          onHide={() => setShowFileList(false)}
          onFileSelected={(file) => {
            setFieldValue('uploadValues.indexFile', file)
            setFieldValue('uploadValues.isZipPhpFile', true)
            if (domainValues) {
              setFieldValue('domainValues.domainSuffix', TLDS.TIINY_IO)
              setFieldValue('domainValues.tlds', [TLDS.TIINY_IO])
            }
            setShowFileList(false)
          }}
          filesList={phpFiles}
          title="Which file should be your homepage?"
        />
      )}
    </>
  )
}

export default SelectedFile
